import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useSession } from "@clerk/clerk-react";
import * as Sentry from "@sentry/react";
import { toast } from "react-toastify";
import { DotsSixVertical, ImageSquare, Info } from "@phosphor-icons/react";
import {
  DndContext,
  DragEndEvent,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  rectSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";
import { Card, CardContent, CardHeader, CardTitle } from "components/ui/card";
import { Skeleton } from "components/ui/skeleton";

interface UnitCommercialPicturesGridProps {
  unitId: string;
}

interface CommercialPicture {
  id: number;
  imageUrl?: string;
  thumbnailUrl?: string;
  fileLabel?: string | null;
  summary?: string;
  displayOrder?: number;
}

const PAGE_SIZE = 50;
// Spacing between consecutive displayOrder values so new uploads can be inserted later.
const ORDER_STEP = 10;

function SortablePicture({ picture }: { picture: CommercialPicture }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: picture.id });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
    zIndex: isDragging ? 10 : undefined,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="group relative aspect-square overflow-hidden rounded-md border border-neutral-primary/10 bg-neutral-primary/5"
    >
      <img
        src={picture.thumbnailUrl || picture.imageUrl}
        alt={picture.summary || picture.fileLabel || `Foto ${picture.id}`}
        loading="lazy"
        className="h-full w-full object-cover"
        draggable={false}
      />
      <button
        type="button"
        {...attributes}
        {...listeners}
        aria-label="Versleep om volgorde te wijzigen"
        className="absolute right-1 top-1 flex h-8 w-8 cursor-grab items-center justify-center rounded-md bg-black/60 text-white opacity-0 transition group-hover:opacity-100 focus:opacity-100 active:cursor-grabbing"
      >
        <DotsSixVertical className="h-5 w-5" weight="bold" />
      </button>
      {picture.fileLabel && (
        <span className="pointer-events-none absolute bottom-0 left-0 right-0 truncate bg-black/50 px-2 py-1 text-xs text-white">
          {String(picture.fileLabel)}
        </span>
      )}
    </div>
  );
}

export const UnitCommercialPicturesGrid = ({
  unitId,
}: UnitCommercialPicturesGridProps) => {
  const { session } = useSession();
  const [pictures, setPictures] = useState<CommercialPicture[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
  );

  const fetchPictures = useCallback(async () => {
    if (!unitId) return;
    setIsLoading(true);
    try {
      const query = new URLSearchParams({
        unitId,
        secondaryLabel: "COMMERCIEEL",
        page: "1",
        pageSize: PAGE_SIZE.toString(),
        includeBuildingImages: "true",
      }).toString();

      const response = await fetchWithAuth(
        `${API_URL}/file/get-images?${query}`,
        { method: "GET" },
        session,
      );
      if (!response.ok) {
        throw new Error(
          `Commerciële foto's ophalen mislukt (${response.status})`,
        );
      }
      const result = await response.json();
      const data: CommercialPicture[] = (result.data ?? result ?? []).filter(
        (p: CommercialPicture) => p.thumbnailUrl || p.imageUrl,
      );
      setPictures(data);
    } catch (error) {
      Sentry.captureException(error, {
        tags: { feature: "unit-detail", tab: "commercial-pictures" },
        extra: { unitId },
      });
      toast.error("Commerciële foto's konden niet worden geladen.");
      console.error("Failed to fetch commercial pictures", error);
    } finally {
      setIsLoading(false);
    }
  }, [unitId, session]);

  useEffect(() => {
    fetchPictures();
  }, [fetchPictures]);

  const persistOrder = useCallback(
    async (ordered: CommercialPicture[], previous: CommercialPicture[]) => {
      setIsSaving(true);
      try {
        // Assign new displayOrder values in ORDER_STEP increments so future inserts have room.
        await Promise.all(
          ordered.map((pic, index) =>
            fetchWithAuth(
              `${API_URL}/file/update-display-order/${pic.id}`,
              {
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                  displayOrder: (index + 1) * ORDER_STEP,
                }),
              },
              session,
            ).then((response) => {
              if (!response.ok) {
                throw new Error(
                  `Volgorde opslaan mislukt voor foto ${pic.id} (${response.status})`,
                );
              }
            }),
          ),
        );
        toast.success("Volgorde opgeslagen.");
      } catch (error) {
        Sentry.captureException(error, {
          tags: { feature: "unit-detail", action: "commercial-pictures-reorder" },
          extra: { unitId, orderedIds: ordered.map((p) => p.id) },
        });
        toast.error("Volgorde kon niet worden opgeslagen. Probeer opnieuw.");
        // Roll back to the previous order so the UI doesn't lie.
        setPictures(previous);
      } finally {
        setIsSaving(false);
      }
    },
    [unitId, session],
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;

      setPictures((current) => {
        const oldIndex = current.findIndex((p) => p.id === active.id);
        const newIndex = current.findIndex((p) => p.id === over.id);
        if (oldIndex === -1 || newIndex === -1) return current;

        const reordered = arrayMove(current, oldIndex, newIndex);
        // Persist the new order against the backend; revert on failure.
        persistOrder(reordered, current);
        return reordered;
      });
    },
    [persistOrder],
  );

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <CardTitle className="flex items-center gap-2 text-lg">
            <ImageSquare className="h-5 w-5" weight="duotone" />
            Commerciële foto's
          </CardTitle>
          <p className="text-xs text-neutral-secondary-mid">
            {isLoading
              ? "Laden..."
              : pictures.length === 0
                ? "Nog geen commerciële foto's"
                : `${pictures.length} foto${pictures.length === 1 ? "" : "'s"} — sleep om de volgorde te wijzigen${
                    isSaving ? " (opslaan...)" : ""
                  }`}
          </p>
        </div>
        <div className="mt-3 flex gap-2 rounded-md border border-moss-primary/20 bg-moss-primary/5 p-3 text-xs text-neutral-primary">
          <Info
            className="mt-0.5 h-4 w-4 shrink-0 text-moss-primary"
            weight="duotone"
            aria-hidden="true"
          />
          <div className="space-y-1">
            <p>
              Dit zijn de foto's die op{" "}
              <a
                href="https://yally.apartments"
                target="_blank"
                rel="noopener noreferrer"
                className="font-medium text-moss-primary hover:underline"
              >
                yally.apartments
              </a>{" "}
              verschijnen, in de volgorde hieronder. Sleep een foto naar een
              andere positie om de publicatievolgorde te wijzigen.
            </p>
            <p className="text-neutral-secondary-mid">
              Een foto verschijnt hier zodra je hem de secundaire label{" "}
              <span className="font-medium text-neutral-primary">
                Commercieel
              </span>{" "}
              geeft — bij het uploaden via{" "}
              <span className="font-medium text-neutral-primary">
                Upload foto
              </span>{" "}
              in de navigatiebalk, of achteraf via de{" "}
              <Link
                to={`/pictures/unit/${unitId}`}
                className="font-medium text-moss-primary hover:underline"
              >
                Foto's
              </Link>
              {" "}pagina (klik op een foto → secundaire label kiezen).
            </p>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6">
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} className="aspect-square w-full" />
            ))}
          </div>
        ) : pictures.length === 0 ? (
          <Link
            to={`/pictures/unit/${unitId}`}
            className="block rounded-md border border-dashed border-neutral-primary/20 p-6 text-center text-sm text-neutral-secondary-mid transition hover:border-moss-primary/40 hover:bg-moss-primary/5 hover:text-moss-primary"
          >
            Nog geen foto's gemarkeerd als commercieel voor deze unit. Klik om
            foto's te bekijken en te taggen.
          </Link>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={pictures.map((p) => p.id)}
              strategy={rectSortingStrategy}
            >
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6">
                {pictures.map((picture) => (
                  <SortablePicture key={picture.id} picture={picture} />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        )}
      </CardContent>
    </Card>
  );
};
